/**
 * Homepage Top 10 finder: pure option builders shared by the card forms and
 * the public read endpoint. Every option is derived from the same production
 * taxonomy that drives the published pages, so the cards cannot drift.
 */
import rankingData from "@/data/ranking_pages.json";
import { DISPLAY_NAME, STAGE_LABEL, STAGE_PHRASE } from "@/lib/ranking-display-names";
import { PROVIDER_CATEGORIES, type ProviderGroupKey } from "@/lib/provider-rankings";

export type Option = { value: string; label: string };

/* ---------------------------------------------------------------- */
/* Companies                                                         */
/* ---------------------------------------------------------------- */

const STAGE_ORDER = ["Seed", "Early Stage", "Later Stage", "PE"];

export const COMPANY_STAGE_OPTIONS: Option[] = STAGE_ORDER.filter((stage) =>
  rankingData.pages.some((p) => p.stage === stage),
).map((stage) => ({ value: stage, label: STAGE_LABEL[stage] ?? stage }));

export const COMPANY_SECTOR_OPTIONS: Option[] = [
  ...new Map(
    rankingData.pages.map((p) => [p.category, DISPLAY_NAME[p.category] ?? p.category]),
  ).entries(),
]
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Stage keys that have a published page for a given sector. */
export function companyStagesForSector(category: string): string[] {
  return STAGE_ORDER.filter((stage) =>
    rankingData.pages.some((p) => p.stage === stage && p.category === category),
  );
}

export function findCompanyPageSlug(stage: string, category: string): string | null {
  const page = rankingData.pages.find((p) => p.stage === stage && p.category === category);
  return page ? page.slug : null;
}

export function companyResultHeading(stage: string, category: string): string {
  const stageWords = STAGE_PHRASE[stage] ?? stage;
  const sector = DISPLAY_NAME[category] ?? category;
  return `Top 10 ${stageWords} investors in ${sector} by 2025 to 2026 deal activity`;
}

/* ---------------------------------------------------------------- */
/* Provider groups                                                   */
/* ---------------------------------------------------------------- */

export function categoryOptions(groupKey: ProviderGroupKey): Option[] {
  return PROVIDER_CATEGORIES.filter((c) => c.groupKey === groupKey).map((c) => ({
    value: c.categoryKey,
    label: c.label,
  }));
}

export const RE_EQUITY_OPTIONS = categoryOptions("re_equity");
export const RE_DEBT_OPTIONS = categoryOptions("re_debt");
export const LOAN_OPTIONS = categoryOptions("loans");

export const FUND_PROFILE_OPTIONS: Array<Option & { groupKey: ProviderGroupKey }> = [
  { value: "funds_established", label: "Established manager", groupKey: "funds_established" },
  { value: "funds_emerging", label: "Emerging manager", groupKey: "funds_emerging" },
  { value: "funds_seeder", label: "Seeking a seeder", groupKey: "funds_seeder" },
];

export const SEEDER_HELPER_TEXT =
  "A seeder is a firm that seeds or anchors a new manager. It does not mean seed-stage venture.";

/* ---------------------------------------------------------------- */
/* Trial handoff                                                     */
/* ---------------------------------------------------------------- */

export type TrialParams = Record<string, string>;

/** Query parameters carried into signup. Step B persists these properly. */
export function trialSearchParams(
  raiseType: "company" | "real_estate" | "fund" | "business_loan",
  answers: Record<string, string | null | undefined>,
): TrialParams {
  const out: TrialParams = { mode: "signup", raise_type: raiseType };
  for (const [key, value] of Object.entries(answers)) {
    if (typeof value === "string" && value.trim()) out[key] = value.trim().slice(0, 120);
  }
  return out;
}

export const TRIAL_COPY =
  "Develop this search and identify the right contacts at these firms inside CapitalDart. Your trial contacts may come from anywhere in the directory, not only this list.";

export const NO_LIST_TEXT = "No published list for these selections yet.";

export const CARD_SUPPORT_TEXT = "Choose a few details. No signup required.";
